import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { bootOnce } from '../../bootstrap';
import { useI18n } from '../../bootstrap/i18n';
import { IconStar } from '../../ui/icons';
import { BottomSheet } from '../../ui/sheets/BottomSheet';
import { getTelegramId, requestAuthRequired } from '../../utils/telegramUser';
import { apiFetch, fetchJson, getAccessTokenOrNull } from '../../utils/api';
import { trackPackageSelected, trackPaywallDismissed, trackPaymentFailed, trackPaymentInitiated, trackTopupOpen } from '../../analytics';
import { reportHandledError } from '../../monitoring/uiErrorSink';
import { getClientSessionId } from '../../utils/clientSession';

type CreditPackageRow = {
  id: number;
  credits: number;
  price_stars: number;
  bonus_text?: string | null;
  is_active?: boolean | null;
  is_popular?: boolean | null;
  is_only_now?: boolean | null;
};

export type TopupSheetContext =
  | { origin: 'storefront' | 'profile' }
  | { 
      origin: 'chat'; 
      modelId: number; 
      modelName?: string; 
      modelAvatarUrl?: string;
      chatSessionId?: string | null; 
      neededCredits: number; 
      commandName: string; 
      balance: number | null 
    };

const PACKAGES_TTL_MS = 60_000;
let packagesCache: { at: number; rows: CreditPackageRow[] } | null = null;
const invoiceCache = new Map<string, string>(); 
const invoiceInflight = new Map<string, Promise<string | null>>();

function getUserIdForPayment(): number | null {
  return getTelegramId();
}

function getFreshPackagesCache(): CreditPackageRow[] | null {
  const c = packagesCache;
  if (!c) return null;
  if (Date.now() - c.at > PACKAGES_TTL_MS) return null;
  if (!Array.isArray(c.rows) || c.rows.length === 0) return null;
  return c.rows;
}

function normalizePackages(pkgs: any[]): CreditPackageRow[] {
  return ((pkgs || []) as any[]).map((p) => ({
    id: Number(p.id),
    credits: Number(p.credits || 0),
    price_stars: Number(p.price_stars || 0),
    bonus_text: p.bonus_text ? String(p.bonus_text) : null,
    is_active: !!p.is_active,
    is_popular: !!p.is_popular,
    is_only_now: !!p.is_only_now,
  })) as CreditPackageRow[];
}

function sortPackagesForUI(packages: CreditPackageRow[]): CreditPackageRow[] {
  return [...packages].sort((a, b) => {
    if (a.is_only_now !== b.is_only_now) return (a.is_only_now ? -1 : 1);
    if (a.is_popular !== b.is_popular) return (a.is_popular ? -1 : 1);
    return b.price_stars - a.price_stars; 
  });
}

export async function warmTopupPackagesCache(services: any): Promise<void> {
  try {
    if (getFreshPackagesCache()) return;
    const r = await fetchJson<{ ok: boolean; data: any[] }>('/api/credit-packages');
    if (!r.ok) return;
    const pkgs = (r.data as any)?.data || [];
    const rows = normalizePackages(pkgs || []);
    if (rows.length > 0) packagesCache = { at: Date.now(), rows };
  } catch {
    // ignore
  }
}

export function TopupSheet(props: {
  open: boolean;
  context: TopupSheetContext;
  onClose: () => void;
}) {
  if (!props.open) return null;
  const services = useMemo(() => bootOnce(), []);
  const { t, locale } = useI18n(services.i18n);

  const initialCached = getFreshPackagesCache();
  const [loading, setLoading] = useState(() => !(initialCached && initialCached.length > 0));
  const [err, setErr] = useState<string | null>(null);
  const [packages, setPackages] = useState<CreditPackageRow[]>(() => initialCached || []);
  const [selectedId, setSelectedId] = useState<number | null>(() => {
    if (!initialCached || initialCached.length === 0) return null;
    return initialCached[0]?.id ?? null;
  });
  
  const [payState, setPayState] = useState<'preparing' | 'ready'>('ready');
  const [offer, setOffer] = useState<any>(null);
  const [offerLoading, setOfferLoading] = useState(false);
  
  // Timestamp-based offer state (ms since epoch)
  const [offerExpiresAt, setOfferExpiresAt] = useState<number>(0);
  const [offerGraceExpiresAt, setOfferGraceExpiresAt] = useState<number>(0);
  const [offerSecondsLeft, setOfferSecondsLeft] = useState<number>(0);
  const [offerGraceSecondsLeft, setOfferGraceSecondsLeft] = useState<number>(0);

  const prefetchDebounceRef = useRef<number | null>(null);
  const timerIntervalRef = useRef<number | null>(null);
  const openedAtRef = useRef<number | null>(null);
  const didAttemptPayRef = useRef(false);
  const didPayRef = useRef(false);
  const didTrackTopupOpenRef = useRef(false);
  const didTrackPackageSelectedRef = useRef(false);

  const selected = useMemo(() => packages.find((p) => p.id === selectedId) || null, [packages, selectedId]);
  
  const isRescueMode = props.context.origin === 'chat';
  const rescueCtx = isRescueMode ? (props.context as any) : null;

  // Compute offer state based on real-time timestamps
  const offerState = useMemo(() => {
    if (offerLoading) return 'loading';
    if (offerSecondsLeft > 0) return 'active';
    if (offerGraceSecondsLeft > 0) return 'grace';
    if (offerExpiresAt > 0 || offerGraceExpiresAt > 0) return 'expired'; // Had offer but it expired
    return 'inactive'; // Never had offer
  }, [offerLoading, offerSecondsLeft, offerGraceSecondsLeft, offerExpiresAt, offerGraceExpiresAt]);

  const { heroPkg, gridPkgs, isFirstDepositActive } = useMemo(() => {
    const isFD = (offerState === 'active' || offerState === 'grace');
    const sorted = sortPackagesForUI(packages);
    const hero = sorted.length > 0 ? sorted[0] : null;
    const rest = sorted.length > 0 ? sorted.slice(1) : [];
    return { heroPkg: hero, gridPkgs: rest, isFirstDepositActive: isFD };
  }, [packages, offerState]);

  // Ensure default selection: always select Hero on open
  useEffect(() => {
    if (props.open && heroPkg) {
      setSelectedId(heroPkg.id);
    }
  }, [props.open]);

  useEffect(() => {
    if (!props.open) return;
    if (didTrackTopupOpenRef.current) return;
    didTrackTopupOpenRef.current = true;
    try {
      const origin = props.context.origin || 'other';
      const modelId = isRescueMode ? Number(rescueCtx?.modelId || 0) : null;
      const chatSessionId = isRescueMode ? String(rescueCtx?.chatSessionId || '') : '';
      trackTopupOpen(origin, {
        modelId: (Number.isFinite(modelId as any) && (modelId as any) > 0) ? (modelId as any) : null,
        chatSessionId: chatSessionId ? chatSessionId : null,
      });
    } catch {}
  }, [props.open]);

  useEffect(() => {
    if (!props.open) return;
    let cancelled = false;
    (async () => {
      setOfferLoading(true);
      const apiBase = services.config.config.apiBaseUrl || '';
      const jwt = await getAccessTokenOrNull(services);
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (jwt) headers['Authorization'] = `Bearer ${jwt}`;
      
      try {
        await apiFetch(`${apiBase}/api/user/first-deposit-offer/start`, { method: 'POST', headers });
      } catch {}
      try {
        const res = await apiFetch(`${apiBase}/api/user/first-deposit-offer`, { method: 'GET', headers });
        if (cancelled) return;
        const data = await res.json().catch(() => ({}));
        setOffer(data);
      } catch {
        if (cancelled) return;
        setOffer(null);
      } finally {
        if (cancelled) return;
        setOfferLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [props.open]);

  // Initialize timestamps from API response
  useEffect(() => {
    if (!offer) {
      setOfferExpiresAt(0);
      setOfferGraceExpiresAt(0);
      setOfferSecondsLeft(0);
      setOfferGraceSecondsLeft(0);
      return;
    }
    
    // Parse ISO timestamps from API
    const expiresAtMs = offer.expires_at ? new Date(offer.expires_at).getTime() : 0;
    const graceExpiresAtMs = offer.grace_expires_at ? new Date(offer.grace_expires_at).getTime() : 0;
    
    setOfferExpiresAt(expiresAtMs);
    setOfferGraceExpiresAt(graceExpiresAtMs);
    
    // Initial calculation
    const now = Date.now();
    setOfferSecondsLeft(Math.max(0, Math.ceil((expiresAtMs - now) / 1000)));
    setOfferGraceSecondsLeft(Math.max(0, Math.ceil((graceExpiresAtMs - now) / 1000)));
  }, [offer]);

  // Timer countdown effect - recalculate from timestamps every second
  useEffect(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    
    // Only run timer if we have valid timestamps and paywall is open
    if (!props.open || (offerExpiresAt <= 0 && offerGraceExpiresAt <= 0)) return;
    
    const tick = () => {
      const now = Date.now();
      const secLeft = Math.max(0, Math.ceil((offerExpiresAt - now) / 1000));
      const graceSecLeft = Math.max(0, Math.ceil((offerGraceExpiresAt - now) / 1000));
      
      setOfferSecondsLeft(secLeft);
      setOfferGraceSecondsLeft(graceSecLeft);
      
      // Stop interval if both expired
      if (secLeft <= 0 && graceSecLeft <= 0 && timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
    
    tick(); // Initial tick
    timerIntervalRef.current = window.setInterval(tick, 1000);
    
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [props.open, offerExpiresAt, offerGraceExpiresAt]);

  useEffect(() => {
    if (props.open) return;
    didTrackTopupOpenRef.current = false;
  }, [props.open]);

  useEffect(() => {
    if (!props.open) return;
    didTrackPackageSelectedRef.current = false;
  }, [props.open]);

  async function prefetchInvoiceLink(packageId: number, userId: number): Promise<string | null> {
    const key = `${userId}:${packageId}`;
    const cached = invoiceCache.get(key);
    if (cached) return cached;

    const inflight = invoiceInflight.get(key);
    if (inflight) return await inflight;

    const promise = (async () => {
      try {
        const apiBaseUrl = services.config.config.apiBaseUrl;
        const clientSessionId = getClientSessionId();
        const res = await apiFetch(`${apiBaseUrl}/api/main/api/create-invoice`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ packageId, userId, locale, clientSessionId }),
        });
        const data = await res.json().catch(() => ({}));
        const link = data?.invoiceLink;
        if (!link) return null;
        invoiceCache.set(key, link);
        return link;
      } catch {
        return null;
      } finally {
        invoiceInflight.delete(key);
      }
    })();

    invoiceInflight.set(key, promise);
    return await promise;
  }

  function openBotBuy(packageId: number) {
    const bot = services.config.config.mainBotUsername;
    const url = `https://t.me/${bot}?start=buy_${packageId}`;
    const tg = (window as any).Telegram?.WebApp;
    if (tg?.openTelegramLink) tg.openTelegramLink(url);
    else window.open(url, '_blank');
  }

  function chooseDefault(pkgs: CreditPackageRow[]) {
    if (!pkgs || pkgs.length === 0) return null;
    const sorted = sortPackagesForUI(pkgs);
    return sorted[0]?.id ?? null;
  }

  useEffect(() => {
    if (!props.open) return;
    openedAtRef.current = Date.now();
    didAttemptPayRef.current = false;
    didPayRef.current = false;
    let cancelled = false;

    (async () => {
      setErr(null);
      setPayState('ready');
      const cachedNow = getFreshPackagesCache();
      if (cachedNow && cachedNow.length > 0) {
        setPackages(cachedNow);
        setSelectedId((prev) => {
          if (prev && cachedNow.some((p) => p.id === prev)) return prev;
          return chooseDefault(cachedNow);
        });
        setLoading(false);
      } else {
        setLoading(true);
      }

      const tg = (window as any).Telegram?.WebApp;
      const telegramId = tg?.initDataUnsafe?.user?.id;
      if (!telegramId) {
        setLoading(false);
        try {
          const path = `${window.location.pathname}${window.location.search || ''}`;
          requestAuthRequired({ path });
        } catch {}
        props.onClose();
        return;
      }

      const r = await fetchJson<{ ok: boolean; data: any[] }>('/api/credit-packages');
      if (cancelled) return;
      if (!r.ok) {
        setErr(reportHandledError({
          err: new Error(`api_error:${r.status || 0}`),
          kind: 'api',
          code: 'topup.packages_failed',
          userMessage: t('topup.error', 'Error loading data'),
          severity: 'error',
          context: { feature: 'topup', endpoint: '/api/credit-packages', status: (r as any).status, resp: (r as any).data },
        }));
        setLoading(false);
        return;
      }

      const rows = normalizePackages((r.data as any)?.data || []);
      packagesCache = { at: Date.now(), rows };
      setPackages(rows);
      setSelectedId((prev) => {
        if (prev && rows.some((p) => p.id === prev)) return prev;
        return chooseDefault(rows);
      });
      setLoading(false);

      const userId = getUserIdForPayment();
      const defId = chooseDefault(rows);
      if (userId && defId) {
        void prefetchInvoiceLink(defId, userId);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [props.open]);

  const onClose = () => {
    const openedAt = openedAtRef.current;
    const timeSpentMs = openedAt ? Math.max(0, Date.now() - openedAt) : 0;
    const origin = props.context.origin;
    if (isRescueMode && !didAttemptPayRef.current && !didPayRef.current) {
      trackPaywallDismissed({ timeSpentMs, origin, packageId: selectedId });
    }
    props.onClose();
  };

  useEffect(() => {
    if (!props.open) return;
    const userId = getUserIdForPayment();
    if (!userId) return;
    if (!selectedId) return;
    if (prefetchDebounceRef.current) window.clearTimeout(prefetchDebounceRef.current);
    prefetchDebounceRef.current = window.setTimeout(() => {
      void prefetchInvoiceLink(selectedId, userId);
    }, 250);
    return () => {
      if (prefetchDebounceRef.current) window.clearTimeout(prefetchDebounceRef.current);
      prefetchDebounceRef.current = null;
    };
  }, [props.open, selectedId]);

  async function pay() {
    const selectedPkg = selected;
    if (!selectedPkg) return;
    didAttemptPayRef.current = true;

    const selectionKind: 'explicit' | 'implicit' = didTrackPackageSelectedRef.current ? 'explicit' : 'implicit';
    trackPaymentInitiated(
      selectedPkg.id,
      selectedPkg.credits,
      selectedPkg.price_stars,
      props.context.origin,
      isRescueMode
        ? { modelId: rescueCtx!.modelId, chatSessionId: rescueCtx!.chatSessionId ?? null }
        : undefined,
      {
        product: 'topup',
        selection_kind: selectionKind,
        offer_state: offerState,
        first_deposit_active: isFirstDepositActive,
        command_name: isRescueMode ? (rescueCtx?.commandName ?? null) : null,
        model_name: isRescueMode ? (rescueCtx?.modelName ?? null) : null,
        needed_credits: isRescueMode ? (Number(rescueCtx?.neededCredits || 0) || null) : null,
        balance: isRescueMode ? (rescueCtx?.balance ?? null) : null,
      }
    );
    
    const tg = (window as any).Telegram?.WebApp;
    const userId = getUserIdForPayment();
    if (!userId) {
      try {
        const path = `${window.location.pathname}${window.location.search || ''}`;
        requestAuthRequired({ path });
      } catch {}
      props.onClose();
      return;
    }

    const key = `${userId}:${selectedPkg.id}`;
    const cached = invoiceCache.get(key);
    
    const openInv = (invLink: string) => {
       if (typeof tg?.openInvoice === 'function') {
        tg.openInvoice(invLink, (status: any) => {
          const st = String(status || '').toLowerCase();
          if (st === 'paid') {
            didPayRef.current = true;
            try { window.dispatchEvent(new Event('aicams-credits-refresh')); } catch {}
            setTimeout(() => { try { window.dispatchEvent(new Event('aicams-credits-refresh')); } catch {} }, 1500);
          } else if (st === 'failed' || st === 'cancelled') {
            trackPaymentFailed({
              packageId: selectedPkg.id,
              credits: selectedPkg.credits,
              priceStars: selectedPkg.price_stars,
              status: st,
              origin: props.context.origin,
              modelId: isRescueMode ? rescueCtx!.modelId : null,
              chatSessionId: isRescueMode ? (rescueCtx!.chatSessionId ?? null) : null,
            });
          }
        });
      } else if (tg?.openTelegramLink) {
        tg.openTelegramLink(invLink);
      } else {
        window.open(invLink, '_blank');
      }
    };

    if (cached) {
      openInv(cached);
      return;
    }

    setPayState('preparing');
    const link = await prefetchInvoiceLink(selectedPkg.id, userId);
    setPayState('ready');
    if (!link) {
      openBotBuy(selectedPkg.id);
      return;
    }
    openInv(link);
  }

  const ctaLabel = (() => {
    if (!selected) return t('topup.select', 'Select a package');
    const credits = Number(selected.credits || 0);
    const price = Number(selected.price_stars || 0);
    const displayedCredits = isFirstDepositActive ? credits * 2 : credits;
    
    return t('topup.cta_deposit', 'Pay {price} ⭐ → Get {credits} ⭐')
      .replace('{price}', price.toLocaleString())
      .replace('{credits}', displayedCredits.toLocaleString());
  })();

  return (
    <BottomSheet open={props.open} onClose={onClose} ariaLabel={t('topup.aria_label', 'Top up')}>
      {/* BACKGROUND: Blurry Avatar or Glow */}
      {isRescueMode && rescueCtx?.modelAvatarUrl ? (
        <div class="headerBlurBg">
            <img src={rescueCtx.modelAvatarUrl} class="headerBlurImg" alt="" />
        </div>
      ) : (
         <div class="tgGateGlow" aria-hidden="true" />
      )}

      <div class="sheetContent">
        
        {/* HEADER: Rescue vs Store */}
        {isRescueMode ? (
          <div class="topupRescueHeader">
            <div class="rescueAvatarRing">
              {rescueCtx?.modelAvatarUrl ? (
                <img src={rescueCtx.modelAvatarUrl} class="rescueAvatar" alt="" />
              ) : (
                <div class="rescueAvatar" style={{ background: '#333' }} />
              )}
            </div>
            <div>
              <div class="rescueTitle">
                {rescueCtx?.modelName 
                  ? t('paywall.rescue.title', "Don't leave {name} waiting...").replace('{name}', rescueCtx.modelName)
                  : t('paywall.title', 'Not enough Stars')}
              </div>
              <div class="rescueSubtitle">
                {t('paywall.rescue.subtitle_model', 'Continue your private chat right now')}
              </div>
            </div>
          </div>
        ) : (
          <div class="topupStoreHeader">
             <div class="storeTitle">{t('topup.title', 'Top up balance')}</div>
             <div class="storeSubtitle">{t('topup.subtitle', 'Stock up on Stars to unlock premium features.')}</div>
          </div>
        )}

        {/* First Deposit Offer Banner - Removed from here as it is integrated into Hero Card logic visually, or we can keep small banner if desired. Keeping hidden for now as Hero takes the stage. */}

        {loading ? <div class="topupLoader">{t('topup.loading', 'Loading packages...')}</div> : null}
        {err ? <div class="topupErrorCard"><div class="topupErrorText">{err}</div></div> : null}

        {!loading && !err ? (
          <div class="topupGrid">
            
            {/* HERO CARD (The "Golden Key") */}
            {heroPkg && (() => {
                const p = heroPkg;
                const selectedNow = p.id === selectedId;
                const credits = Number(p.credits || 0);
                const price = Number(p.price_stars || 0);
                const dispCredits = isFirstDepositActive ? credits * 2 : credits;
                
                // Hero Bonus Calc
                const rate = price > 0 ? credits / price : 1;
                const bonusPct = Math.round((rate - 1) * 100);
                let bonusLabel = isFirstDepositActive ? t('topup.bonus_free', '+100% FREE') : (p.bonus_text || (bonusPct > 5 ? `+${bonusPct}%` : null));

                return (
                    <button
                        key={p.id}
                        class={`heroCard ${selectedNow ? 'selected' : ''} ${isFirstDepositActive ? 'firstDeposit' : ''}`}
                        onClick={() => {
                            setSelectedId(p.id);
                            didTrackPackageSelectedRef.current = true;
                            trackPackageSelected(
                                p.id, credits, price, props.context.origin,
                                isRescueMode ? { modelId: rescueCtx!.modelId, chatSessionId: rescueCtx!.chatSessionId ?? null } : undefined
                            );
                        }}
                    >
                        {/* Hero Badge */}
                        {isFirstDepositActive ? (
                            <div class="heroBadge" style={{ background: '#FFD700', color: '#000' }}>
                                {t('topup.first_deposit', 'First Deposit Bonus')}
                            </div>
                        ) : p.is_only_now ? (
                            <div class="heroBadge" style={{ background: 'linear-gradient(90deg, #ff3b30, #dc143c)', color: '#fff' }}>
                                {t('topup.ribbon_only_now', 'ONLY NOW')}
                            </div>
                        ) : p.is_popular ? (
                            <div class="heroBadge">
                                {t('topup.ribbon_popular', 'POPULAR')}
                            </div>
                        ) : null}

                        {/* Timer (if FD active) or Last Chance (if grace) */}
                        {isFirstDepositActive && offerSecondsLeft > 0 ? (
                            <div class="heroTimer">
                                <span>⏱</span>
                                {String(Math.floor(offerSecondsLeft / 60)).padStart(2, '0')}:{String(offerSecondsLeft % 60).padStart(2, '0')}
                            </div>
                        ) : isFirstDepositActive && offerState === 'grace' ? (
                            <div class="heroTimer" style={{ background: 'rgba(255, 165, 0, 0.25)', borderColor: 'rgba(255, 165, 0, 0.4)', color: '#ffa500' }}>
                                <span>🔥</span>
                                {t('topup.last_chance', 'LAST CHANCE!')}
                            </div>
                        ) : null}

                        <div class="heroContent">
                            <div class="heroLeft">
                                <div class="heroAmount">
                                    {dispCredits.toLocaleString()}
                                    <IconStar class="heroAmountSvg" filled />
                                </div>
                                <div class="productLabel" style={{ color: 'rgba(255,255,255,0.6)', marginTop: -4 }}>
                                  {t('topup.credits_label_caps', 'CREDITS')}
                                </div>
                                {bonusLabel && <div class="heroBonus" style={{ marginTop: 8 }}>{bonusLabel}</div>}
                            </div>
                            
                            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
                                <div class="heroPriceBtn">
                                    <span style={{ marginRight: 6, fontSize: '0.9em', opacity: 0.9 }}>
                                      {t('topup.buy_for', 'BUY FOR')}
                                    </span>
                                    {price.toLocaleString()}
                                    <span style={{ opacity: 0.8, marginLeft: 2, display: 'inline-flex' }}>
                                      <IconStar class="priceBtnStar" filled size={18} />
                                    </span>
                                </div>
                                <div class="trustBadge">
                                    <span style={{ fontSize: 10, opacity: 0.6 }}>
                                      {t('topup.trust_badge', 'Official Telegram Payment • Secure')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </button>
                );
            })()}

            {/* GRID CARDS (Standard Options) */}
            {gridPkgs.map((p) => {
                const selectedNow = p.id === selectedId;
                const credits = Number(p.credits || 0);
                const price = Number(p.price_stars || 0);
                const dispCredits = isFirstDepositActive ? credits * 2 : credits;
                
                const rate = price > 0 ? credits / price : 1;
                const bonusPct = Math.round((rate - 1) * 100);
                let bonusLabel = isFirstDepositActive ? '+100%' : (p.bonus_text || (bonusPct > 5 ? `+${bonusPct}%` : null));

                return (
                    <button
                        key={p.id}
                        class={`gridCard ${selectedNow ? 'selected' : ''}`}
                        onClick={() => {
                            setSelectedId(p.id);
                            didTrackPackageSelectedRef.current = true;
                            trackPackageSelected(
                                p.id, credits, price, props.context.origin,
                                isRescueMode ? { modelId: rescueCtx!.modelId, chatSessionId: rescueCtx!.chatSessionId ?? null } : undefined
                            );
                        }}
                    >
                       {p.is_popular && !isFirstDepositActive && (
                           <div class="gridBadge">{t('topup.ribbon_popular', 'POPULAR')}</div>
                       )}
                       
                       <div class="gridTop">
                           <div class="gridAmount">
                               {dispCredits.toLocaleString()}
                               <span style={{ color: '#FFD700', display: 'inline-flex' }}>
                                 <IconStar size={18} filled />
                               </span>
                           </div>
                           <div class="productLabel">{t('topup.credits_label', 'Credits')}</div>
                           {bonusLabel && <div class="gridBonus">{bonusLabel}</div>}
                       </div>

                       <div class="gridPriceBtn">
                           <span style={{ marginRight: 3, opacity: 0.7, fontSize: '0.85em', fontWeight: 800 }}>
                             {t('topup.pay', 'PAY')}
                           </span>
                           {price.toLocaleString()}
                           <IconStar class="priceBtnStar" filled />
                       </div>
                    </button>
                );
            })}

          </div>
        ) : null}
      </div>

      <div class="sheetFooter">
        <button class="primaryBtn tgGateCta" style={{ width: '100%' }} disabled={!selected || payState === 'preparing'} onClick={pay}>
          {payState === 'preparing' ? (
             t('topup.preparing', 'Preparing invoice…')
          ) : (
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              {isFirstDepositActive || selected?.is_only_now ? (
                 <span style={{ 
                   backgroundColor: '#000', color: '#FFD700', 
                   fontSize: 10, fontWeight: 900, padding: '2px 6px', borderRadius: 4, 
                   textTransform: 'uppercase', letterSpacing: 0.5
                 }}>
                   {isFirstDepositActive ? t('topup.badge_bonus', 'BONUS') : t('topup.ribbon_only_now', 'ONLY NOW')}
                 </span>
              ) : null}
              <span>{ctaLabel}</span>
            </span>
          )}
        </button>
        <div class="tgGateHint">{t('topup.hint', 'Telegram will show a quick Stars confirmation — one tap and you’re back.')}</div>
      </div>
    </BottomSheet>
  );
}
